import { t } from "../i18n";

/**
 * Client-side checks before preview (blob:) or upload. Mirrors server allowed types; does not trust filename/MIME.
 * Prevents SVG/script payloads from being previewed via blob URLs (INSA / stored XSS surface).
 */

const PNG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const HEIF_BRANDS = new Set(["heic", "heix", "hevc", "hevx", "mif1", "msf1"]);

function asciiAt(u8, offset, four) {
  if (u8.length < offset + 4) return false;
  for (let i = 0; i < 4; i++) {
    if (u8[offset + i] !== four.charCodeAt(i)) return false;
  }
  return true;
}

function asciiPrefix(u8, s) {
  const enc = new TextEncoder().encode(s);
  if (u8.length < enc.length) return false;
  for (let i = 0; i < enc.length; i++) {
    if (u8[i] !== enc[i]) return false;
  }
  return true;
}

function readBrand(u8, offset) {
  if (u8.length < offset + 4) return "";
  return String.fromCharCode(u8[offset], u8[offset + 1], u8[offset + 2], u8[offset + 3]).toLowerCase();
}

/** @returns {'png'|'gif'|'jpeg'|'webp'|'heif'|null} */
export function sniffAllowedImageKind(uint8) {
  const n = uint8.length;
  if (n < 12) return null;

  if (n >= PNG.length && PNG.every((b, i) => uint8[i] === b)) return "png";

  if (n >= 6 && (asciiPrefix(uint8, "GIF87a") || asciiPrefix(uint8, "GIF89a"))) return "gif";

  if (
    n >= 3 &&
    uint8[0] === 0xff &&
    uint8[1] === 0xd8 &&
    uint8[2] === 0xff
  ) {
    return "jpeg";
  }

  if (asciiPrefix(uint8, "RIFF") && asciiAt(uint8, 8, "WEBP")) return "webp";

  if (asciiAt(uint8, 4, "ftyp")) {
    const brand = readBrand(uint8, 8);
    if (HEIF_BRANDS.has(brand)) return "heif";
  }

  return null;
}

/**
 * Ensure file is safe for merchant portal (preview blob + upload). Uses magic bytes + browser decode where possible.
 * @param {File} file
 * @param {{ maxBytes?: number }} opts
 */
export async function validateMerchantPortalImageFile(file, opts = {}) {
  const maxBytes = opts.maxBytes ?? 10 * 1024 * 1024;
  if (!(file instanceof File)) return { ok: false, message: t("file.none_selected") };
  if (file.size <= 0) return { ok: false, message: t("file.empty") };
  if (file.size > maxBytes) return { ok: false, message: t("file.too_large", { mb: Math.round(maxBytes / (1024 * 1024)) }) };

  let head;
  try {
    const slice = await file.slice(0, Math.min(file.size, 64)).arrayBuffer();
    head = new Uint8Array(slice);
  } catch {
    return { ok: false, message: t("file.unreadable") };
  }

  const kind = sniffAllowedImageKind(head);
  if (!kind) {
    return {
      ok: false,
      message: t("file.image_type"),
    };
  }

  if (kind === "heif" || kind === "webp") {
    return { ok: true, kind };
  }

  if (typeof createImageBitmap !== "function") {
    return { ok: false, message: t("file.browser_cannot_validate") };
  }

  try {
    const bmp = await createImageBitmap(file);
    bmp.close();
    return { ok: true, kind };
  } catch {
    return { ok: false, message: t("file.image_corrupt") };
  }
}

/**
 * Create a blob: URL only after validation. Caller must revoke when done.
 * @returns {Promise<{ url: string, kind: string } | { error: string }>}
 */
export async function createValidatedImageObjectUrl(file, opts) {
  const v = await validateMerchantPortalImageFile(file, opts);
  if (!v.ok) return { error: v.message };
  return { url: URL.createObjectURL(file), kind: v.kind };
}

/**
 * Block dangerous image URL values from API (SVG-as-image edge cases, javascript:, data: HTML).
 * @param {string|null|undefined} url
 * @param {string} fallbackUrl
 */
export function safeProductOrProfileImageSrc(url, fallbackUrl) {
  if (url == null || typeof url !== "string") return fallbackUrl;
  const t = url.trim();
  if (!t) return fallbackUrl;
  const lower = t.split("?")[0].toLowerCase();
  if (lower.startsWith("javascript:") || lower.startsWith("data:text/html") || lower.startsWith("data:image/svg")) {
    return fallbackUrl;
  }
  if (lower.endsWith(".svg") || lower.endsWith(".svgz") || lower.endsWith(".html") || lower.endsWith(".htm")) {
    return fallbackUrl;
  }
  return url;
}
